import type { ComponentProps, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export const Card: FC<ComponentProps<'section'>> = ({ className, ...props }): ReactNode => {
    return (
        <section
            className={cn('min-w-0 rounded-xl border bg-card text-card-foreground shadow-card', className)}
            {...props}
        />
    )
}

type CardHeaderProps = {
    title: ReactNode
    description?: ReactNode
    eyebrow?: ReactNode
    icon?: ReactNode
    action?: ReactNode
    titleId?: string
    className?: string
}

export const CardHeader: FC<CardHeaderProps> = ({
    title,
    description,
    eyebrow,
    icon,
    action,
    titleId,
    className
}): ReactNode => {
    return (
        <header className={cn('flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b px-5 py-4', className)}>
            <div className='flex min-w-0 flex-1 items-start gap-3'>
                {icon && (
                    <span className='grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary [&_svg]:size-[18px]' aria-hidden='true'>
                        {icon}
                    </span>
                )}

                <div className='min-w-0'>
                    {eyebrow && (
                        <p className='mb-0.5 text-[11px] font-semibold tracking-[0.12em] text-primary uppercase'>
                            {eyebrow}
                        </p>
                    )}

                    <h2 id={titleId} className='text-[15px] leading-snug font-semibold tracking-tight text-foreground'>
                        {title}
                    </h2>

                    {description && (
                        <p className='mt-0.5 text-[13px] leading-relaxed text-muted-foreground'>
                            {description}
                        </p>
                    )}
                </div>
            </div>

            {action && (
                <div className='flex shrink-0 flex-wrap items-center gap-2'>
                    {action}
                </div>
            )}
        </header>
    )
}

export const CardContent: FC<ComponentProps<'div'>> = ({ className, ...props }): ReactNode => {
    return <div className={cn('p-5', className)} {...props} />
}

export const CardFooter: FC<ComponentProps<'div'>> = ({ className, ...props }): ReactNode => {
    return (
        <div
            className={cn('flex flex-wrap items-center justify-between gap-3 border-t px-5 py-4', className)}
            {...props}
        />
    )
}
